<template>
    <div class="drawer drawer-end">
        <input id="plugin-config-drawer" type="checkbox" class="drawer-toggle" v-model="visible" />
        <div class="drawer-side">
            <label for="plugin-config-drawer" aria-label="close sidebar" class="drawer-overlay"></label>
            <div class="p-4 min-h-full bg-base-100 text-base-content w-96 md:w-[500px]">
                <div class="flex justify-between items-center mb-6">
                    <div class="flex items-center space-x-3">
                        <div class="avatar placeholder">
                            <div class="bg-neutral text-neutral-content rounded-full w-12">
                                <span class="text-xl">{{ pluginIcon }}</span>
                            </div>
                        </div>
                        <div>
                            <h2 class="text-xl font-bold">{{ plugin.name }}</h2>
                            <p class="text-sm text-base-content/70">{{ plugin.description }}</p>
                        </div>
                    </div>
                    <button class="btn btn-sm btn-circle btn-ghost" @click="close">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <!-- 插件信息 -->
                <div class="card bg-base-200 shadow-sm mb-6">
                    <div class="card-body p-4">
                        <h3 class="card-title text-lg mb-3">插件信息</h3>
                        <div class="space-y-2">
                            <div class="flex justify-between">
                                <span class="text-sm text-base-content/70">版本</span>
                                <div class="badge badge-primary badge-sm">v{{ plugin.version }}</div>
                            </div>
                            <div class="flex justify-between">
                                <span class="text-sm text-base-content/70">状态</span>
                                <div :class="['badge', 'badge-sm','text-white', plugin.state === 'online' ? 'badge-success' : 'badge-warning']">
                                    {{ plugin.state === 'online' ? '在线' : '离线' }}
                                </div>
                            </div>
                            <div class="flex justify-between">
                                <span class="text-sm text-base-content/70">类型</span>
                                <span class="text-sm">{{ pluginType === 'tagSource' ? '标签源插件' : '操作台插件' }}</span>
                            </div>
                            <div class="flex justify-between">
                                <span class="text-sm text-base-content/70">标识</span>
                                <span class="text-sm font-mono">{{ plugin.unique_id }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 插件配置 -->
                <div class="card bg-base-100 shadow-sm mb-6">
                    <div class="card-body p-4">
                        <h3 class="card-title text-lg mb-4">插件配置</h3>
                        
                        <!-- 基础开关 -->
                        <div class="form-control mb-4">
                            <label class="label cursor-pointer">
                                <span class="label-text">启用插件</span>
                                <input type="checkbox" class="toggle toggle-primary" v-model="configForm.enabled" />
                            </label>
                        </div>

                        <!-- 优先级配置 -->
                        <div class="form-control mb-4">
                            <label class="label">
                                <span class="label-text">优先级</span>
                                <span class="label-text-alt">数值越小优先级越高</span>
                            </label>
                            <input type="number" class="input input-bordered input-sm" v-model="configForm.priority" min="0" max="100" />
                        </div>

                        <!-- 标签源插件特有配置 -->
                        <div v-if="pluginType === 'tagSource'">
                            <div class="form-control mb-4">
                                <label class="label">
                                    <span class="label-text">匹配超时时间</span>
                                    <span class="label-text-alt">秒</span>
                                </label>
                                <input type="number" class="input input-bordered input-sm" v-model="configForm.timeout" min="5" max="300" />
                            </div>

                            <div class="form-control mb-4">
                                <label class="label cursor-pointer">
                                    <span class="label-text">自动匹配歌词</span>
                                    <input type="checkbox" class="toggle toggle-primary" v-model="configForm.autoLyrics" />
                                </label>
                            </div>

                            <div class="form-control mb-4">
                                <label class="label cursor-pointer">
                                    <span class="label-text">自动匹配封面</span>
                                    <input type="checkbox" class="toggle toggle-primary" v-model="configForm.autoCover" />
                                </label>
                            </div>

                            <div class="form-control mb-4">
                                <label class="label">
                                    <span class="label-text">匹配字段</span>
                                </label>
                                <div class="grid grid-cols-2 gap-2">
                                    <label class="label cursor-pointer" v-for="field in tagFields" :key="field.id">
                                        <span class="label-text text-sm">{{ field.name }}</span>
                                        <input type="checkbox" class="checkbox checkbox-sm" v-model="configForm.enabledFields" :value="field.id" />
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- 操作台插件特有配置 -->
                        <div v-if="pluginType === 'action'">
                            <div class="form-control mb-4">
                                <label class="label cursor-pointer">
                                    <span class="label-text">批量操作模式</span>
                                    <input type="checkbox" class="toggle toggle-primary" v-model="configForm.batchMode" />
                                </label>
                            </div>

                            <div class="form-control mb-4">
                                <label class="label">
                                    <span class="label-text">最大并发数</span>
                                </label>
                                <input type="number" class="input input-bordered input-sm" v-model="configForm.maxConcurrency" min="1" max="10" />
                            </div>

                            <div class="form-control mb-4">
                                <label class="label cursor-pointer">
                                    <span class="label-text">自动备份</span>
                                    <input type="checkbox" class="toggle toggle-primary" v-model="configForm.autoBackup" />
                                </label>
                            </div>
                        </div>

                        <!-- 自定义配置 -->
                        <div class="form-control mb-4">
                            <label class="label">
                                <span class="label-text">自定义配置</span>
                                <span class="label-text-alt">JSON格式</span>
                            </label>
                            <textarea 
                                class="textarea textarea-bordered h-24" 
                                v-model="configForm.customConfig" 
                                placeholder='{"key": "value"}'
                                @blur="validateJSON"
                            ></textarea>
                            <div v-if="jsonError" class="text-error text-xs mt-1">{{ jsonError }}</div>
                        </div>
                    </div>
                </div>

                <!-- 高级设置 -->
                <div class="collapse collapse-arrow bg-base-200 mb-6">
                    <input type="checkbox" />
                    <div class="collapse-title text-lg font-medium">
                        高级设置
                    </div>
                    <div class="collapse-content">
                        <div class="form-control mb-4">
                            <label class="label">
                                <span class="label-text">调试模式</span>
                            </label>
                            <select class="select select-bordered select-sm" v-model="configForm.debugLevel">
                                <option value="none">关闭</option>
                                <option value="error">错误</option>
                                <option value="warn">警告</option>
                                <option value="info">信息</option>
                                <option value="debug">调试</option>
                            </select>
                        </div>

                        <div class="form-control mb-4">
                            <label class="label">
                                <span class="label-text">重试次数</span>
                            </label>
                            <input type="number" class="input input-bordered input-sm" v-model="configForm.retryCount" min="0" max="5" />
                        </div>

                        <div class="form-control mb-4">
                            <label class="label cursor-pointer">
                                <span class="label-text">缓存结果</span>
                                <input type="checkbox" class="toggle toggle-primary" v-model="configForm.cacheEnabled" />
                            </label>
                        </div>
                    </div>
                </div>

                <!-- 操作按钮 -->
                <div class="flex justify-between items-center space-x-2">
                    <div class="flex space-x-2">
                        <button class="btn btn-outline btn-sm" @click="testPlugin" :disabled="!configForm.enabled">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                            测试
                        </button>
                        <button class="btn btn-outline btn-sm" @click="resetConfig">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            重置
                        </button>
                    </div>
                    <div class="flex space-x-2">
                        <button class="btn btn-outline btn-sm" @click="close">取消</button>
                        <button class="btn btn-primary btn-sm" @click="saveConfig" :disabled="jsonError">
                            <span class="loading loading-spinner loading-sm" v-if="saving"></span>
                            保存
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PluginConfigDrawer',
    props: {
        plugin: {
            type: Object,
            default: () => ({})
        },
        pluginType: {
            type: String,
            default: 'tagSource'
        },
        show: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            visible: false,
            saving: false,
            jsonError: '',
            configForm: {
                enabled: true,
                priority: 50,
                timeout: 30,
                autoLyrics: true,
                autoCover: true,
                enabledFields: [],
                batchMode: false,
                maxConcurrency: 3,
                autoBackup: true,
                debugLevel: 'none',
                retryCount: 2,
                cacheEnabled: true,
                customConfig: '{}'
            },
            tagFields: [
                { id: 'title', name: '标题' },
                { id: 'artist', name: '艺术家' },
                { id: 'album', name: '专辑' },
                { id: 'albumartist', name: '专辑艺术家' },
                { id: 'year', name: '年份' },
                { id: 'genre', name: '风格' },
                { id: 'tracknumber', name: '音轨号' },
                { id: 'discnumber', name: '光盘号' },
                { id: 'lyrics', name: '歌词' },
                { id: 'cover', name: '封面' }
            ]
        }
    },
    computed: {
        pluginIcon() {
            if (this.pluginType === 'tagSource') {
                return '🏷️'
            } else {
                return '⚙️'
            }
        }
    },
    watch: {
        show(newVal) {
            this.visible = newVal
            if (newVal) {
                this.loadConfig()
            }
        },
        visible(newVal) {
            if (!newVal) {
                this.$emit('update:show', false)
            }
        }
    },
    methods: {
        loadConfig() {
            // 从插件数据加载配置
            if (this.plugin.config) {
                try {
                    const config = typeof this.plugin.config === 'string' 
                        ? JSON.parse(this.plugin.config) 
                        : this.plugin.config
                    
                    this.configForm = {
                        ...this.configForm,
                        ...config,
                        enabled: this.plugin.state === 'online'
                    }
                } catch (error) {
                    console.error('配置解析错误:', error)
                }
            }
        },

        validateJSON() {
            if (!this.configForm.customConfig.trim()) {
                this.configForm.customConfig = '{}'
                this.jsonError = ''
                return
            }

            try {
                JSON.parse(this.configForm.customConfig)
                this.jsonError = ''
            } catch (error) {
                this.jsonError = 'JSON格式错误: ' + error.message
            }
        },

        async testPlugin() {
            try {
                this.$myMsg.notify({
                    title: '测试插件中...',
                    type: 'info',
                    time: 2000
                })
                
                // 这里调用测试插件的API
                // await this.$api.Plugin.test(this.plugin.id, this.configForm)
                
                this.$myMsg.notify({
                    title: '插件测试成功',
                    type: 'success',
                    time: 2000
                })
            } catch (error) {
                this.$myMsg.notify({
                    title: '插件测试失败',
                    message: error.message,
                    type: 'error',
                    time: 3000
                })
            }
        },

        resetConfig() {
            this.configForm = {
                enabled: true,
                priority: 50,
                timeout: 30,
                autoLyrics: true,
                autoCover: true,
                enabledFields: ['title', 'artist', 'album'],
                batchMode: false,
                maxConcurrency: 3,
                autoBackup: true,
                debugLevel: 'none',
                retryCount: 2,
                cacheEnabled: true,
                customConfig: '{}'
            }
            this.jsonError = ''
        },

        async saveConfig() {
            if (this.jsonError) return

            this.saving = true
            try {
                const config = {
                    ...this.configForm,
                    customConfig: JSON.parse(this.configForm.customConfig)
                }

                this.$emit('config-updated', {
                    plugin: this.plugin,
                    config: config,
                    enabled: this.configForm.enabled
                })

                this.$myMsg.notify({
                    title: '配置保存成功',
                    type: 'success',
                    time: 2000
                })

                this.close()
            } catch (error) {
                this.$myMsg.notify({
                    title: '保存失败',
                    message: error.message,
                    type: 'error',
                    time: 3000
                })
            } finally {
                this.saving = false
            }
        },

        close() {
            this.visible = false
        }
    }
}
</script>

<style scoped>
.h-screen-header {
    height: calc(100vh - 4rem);
}
</style> 